import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ArrowUpRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FREE_EMAIL_DOMAINS = [
  'gmail.com', 'googlemail.com', 'yahoo.com', 'yahoo.co.in', 'yahoo.co.uk',
  'hotmail.com', 'hotmail.co.uk', 'outlook.com', 'live.com', 'msn.com',
  'aol.com', 'icloud.com', 'me.com', 'mac.com', 'protonmail.com', 'proton.me',
  'gmx.com', 'gmx.net', 'zoho.com', 'yandex.com', 'mail.com', 'rediffmail.com',
];

const HIGHLIGHTS = [
  { t: 'Bespoke to your brand', d: 'Custom weaves, signature colours, monogrammed packaging and hand-written cards curated for your recipients.' },
  { t: 'Handcrafted at scale', d: 'A curated network of ateliers can honour volumes of 30 to 3,000+ pieces while preserving the integrity of the craft.' },
  { t: 'Global fulfilment', d: 'Fully-managed logistics with door-to-door delivery in 40+ countries, gift-ready with concierge support.' },
];

const OCCASIONS = [
  'Client gifting & retention',
  'Employee milestones & appreciation',
  'Executive & board gifting',
  'Wedding, festive & family concierge',
  'Hospitality & retail programmes',
  'Custom collaborations & private label',
];

const CorporateEnquiriesPage = () => {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    company_name: '', email: '', products_required: '', quantity: '',
    required_by: '', contact_name: '', phone: '', message: '',
  });

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const validate = () => {
    if (!form.company_name.trim()) { toast.error('Company name is required.'); return false; }
    const email = form.email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { toast.error('Please enter a valid email address.'); return false; }
    const domain = email.split('@')[1];
    if (FREE_EMAIL_DOMAINS.includes(domain)) {
      toast.error('Please use a business email. Personal email domains (Gmail, Yahoo, Hotmail, etc.) are not accepted.');
      return false;
    }
    if (!form.products_required.trim()) { toast.error('Please describe the products required.'); return false; }
    const qty = parseInt(form.quantity, 10);
    if (!qty || qty < 30) { toast.error('Minimum order quantity is 30 units.'); return false; }
    if (!form.required_by) { toast.error('Please pick a required-by date.'); return false; }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/corporate/enquire`, {
        company_name: form.company_name.trim(),
        email: form.email.trim().toLowerCase(),
        products_required: form.products_required.trim(),
        quantity: parseInt(form.quantity, 10),
        required_by: form.required_by,
        contact_name: form.contact_name.trim() || null,
        phone: form.phone.trim() || null,
        message: form.message.trim() || null,
      });
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Something went wrong. Please try again or write to corporate@chaleur.online.';
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="pt-24" data-testid="corporate-enquiries-page">
      {/* Hero */}
      <section className="chaleur-container py-20 md:py-28">
        <div className="max-w-3xl">
          <p className="text-xs tracking-[0.3em] uppercase text-muted-foreground mb-6" data-testid="corporate-eyebrow">
            Corporate &amp; Bulk Orders
          </p>
          <h1 className="editorial-italic text-4xl md:text-5xl lg:text-6xl leading-[1.05] mb-8" data-testid="corporate-title">
            Gifts that carry your name — <br />and someone's craft.
          </h1>
          <p className="text-base md:text-lg text-muted-foreground leading-relaxed max-w-2xl">
            From considered client gifting to full-scale corporate programmes, Chaleur curates
            handcrafted scarves, wraps, art and gourmet pieces made in India — packaged, personalised
            and delivered worldwide. Every piece supports the artisan communities behind it.
          </p>
        </div>
      </section>

      {/* Highlights */}
      <section className="border-y border-border/60 bg-secondary/30">
        <div className="chaleur-container py-16 md:py-20 grid md:grid-cols-3 gap-10 md:gap-14">
          {HIGHLIGHTS.map((h, i) => (
            <div key={h.t} data-testid={`corporate-highlight-${i}`}>
              <p className="text-xs tracking-[0.3em] uppercase text-muted-foreground mb-3">0{i + 1}</p>
              <h3 className="text-xl md:text-2xl mb-3 font-light">{h.t}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{h.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Occasions + Form */}
      <section className="chaleur-container py-20 md:py-28">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Occasions */}
          <div className="lg:col-span-5">
            <p className="text-xs tracking-[0.3em] uppercase text-muted-foreground mb-6">Programmes we curate</p>
            <h2 className="editorial-italic text-3xl md:text-4xl leading-tight mb-8">
              One conversation.<br />A hundred thoughtful gifts.
            </h2>
            <ul className="space-y-4">
              {OCCASIONS.map((o) => (
                <li key={o} className="flex items-start gap-3 text-sm">
                  <span className="mt-1 w-1.5 h-1.5 bg-primary flex-shrink-0" />
                  <span className="text-foreground/80">{o}</span>
                </li>
              ))}
            </ul>
            <div className="mt-10 p-6 border border-border bg-background">
              <p className="text-xs tracking-[0.3em] uppercase text-muted-foreground mb-2">Minimum order</p>
              <p className="text-sm text-foreground/80 leading-relaxed">
                Corporate orders start at <b>30 units</b>. Larger volumes unlock deeper personalisation and priority production windows.
              </p>
            </div>
          </div>

          {/* Form / Confirmation */}
          <div className="lg:col-span-7">
            {submitted ? (
              <div
                className="border border-border p-10 md:p-14 bg-background"
                data-testid="corporate-thankyou"
              >
                <p className="text-xs tracking-[0.3em] uppercase text-muted-foreground mb-6">Enquiry received</p>
                <h2 className="editorial-italic text-3xl md:text-4xl leading-tight mb-6">
                  Thank you. We'll be in touch shortly.
                </h2>
                <p className="text-sm md:text-base text-muted-foreground leading-relaxed mb-6">
                  A member of our corporate desk will personally review your enquiry and respond
                  with indicative pricing, timelines and next steps within two working days. We've
                  also sent a confirmation to <b>{form.email}</b>.
                </p>
                <p className="text-sm text-muted-foreground">
                  For anything urgent, please write to{' '}
                  <a href="mailto:corporate@chaleur.online" className="underline hover:text-foreground">corporate@chaleur.online</a>.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} noValidate className="space-y-6" data-testid="corporate-form">
                <div className="mb-2">
                  <p className="text-xs tracking-[0.3em] uppercase text-muted-foreground mb-3">Begin the conversation</p>
                  <h2 className="editorial-italic text-3xl md:text-4xl leading-tight">Tell us what you have in mind.</h2>
                </div>

                <div>
                  <Label htmlFor="company_name">Company name *</Label>
                  <Input
                    id="company_name" type="text" value={form.company_name}
                    onChange={(e) => update('company_name', e.target.value)}
                    required data-testid="corp-company-name"
                    className="rounded-none h-12 mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="email">Business email *</Label>
                  <Input
                    id="email" type="email" value={form.email}
                    onChange={(e) => update('email', e.target.value)}
                    required data-testid="corp-email"
                    className="rounded-none h-12 mt-2"
                    placeholder="you@yourcompany.com"
                  />
                  <p className="text-xs text-muted-foreground mt-1.5">
                    Personal email domains (Gmail, Yahoo, Hotmail, etc.) are not accepted.
                  </p>
                </div>

                <div>
                  <Label htmlFor="products_required">Products required *</Label>
                  <Textarea
                    id="products_required" value={form.products_required}
                    onChange={(e) => update('products_required', e.target.value)}
                    required data-testid="corp-products"
                    className="rounded-none mt-2 min-h-[100px]"
                    placeholder="e.g. Silk scarves in brand colours, monogrammed clutch wallets, festive gourmet hampers…"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="quantity">Quantity * (MOQ 30)</Label>
                    <Input
                      id="quantity" type="number" min="30" step="1" value={form.quantity}
                      onChange={(e) => update('quantity', e.target.value)}
                      required data-testid="corp-quantity"
                      className="rounded-none h-12 mt-2"
                      placeholder="30"
                    />
                  </div>
                  <div>
                    <Label htmlFor="required_by">Required by *</Label>
                    <Input
                      id="required_by" type="date" value={form.required_by} min={today}
                      onChange={(e) => update('required_by', e.target.value)}
                      required data-testid="corp-required-by"
                      className="rounded-none h-12 mt-2"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6 pt-2 border-t border-border/60">
                  <div>
                    <Label htmlFor="contact_name">Your name (optional)</Label>
                    <Input
                      id="contact_name" type="text" value={form.contact_name}
                      onChange={(e) => update('contact_name', e.target.value)}
                      data-testid="corp-contact-name"
                      className="rounded-none h-12 mt-2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone (optional)</Label>
                    <Input
                      id="phone" type="tel" value={form.phone}
                      onChange={(e) => update('phone', e.target.value)}
                      data-testid="corp-phone"
                      className="rounded-none h-12 mt-2"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="message">Anything else we should know? (optional)</Label>
                  <Textarea
                    id="message" value={form.message}
                    onChange={(e) => update('message', e.target.value)}
                    data-testid="corp-message"
                    className="rounded-none mt-2 min-h-[90px]"
                    placeholder="Occasion, budget range, personalisation, delivery locations…"
                  />
                </div>

                <Button
                  type="submit" disabled={submitting}
                  className="btn-chaleur-primary w-full md:w-auto"
                  data-testid="corp-submit-btn"
                >
                  {submitting ? 'Sending…' : (<>Send enquiry <ArrowUpRight className="w-4 h-4 ml-2" /></>)}
                </Button>
                <p className="text-xs text-muted-foreground">
                  By submitting, you agree to be contacted by the Chaleur corporate desk regarding this enquiry.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default CorporateEnquiriesPage;
